import { motion } from 'framer-motion';
import { FileText, Gavel, Users } from 'lucide-react';
import type { WalletTag } from '@/types/walletIntel';
import { CATEGORY_LABEL, CATEGORY_COLOR, SOURCE_ADMIRALTY_GRADE } from '@/types/walletIntel';
import { AdmiraltyBadge } from './AdmiraltyBadge';

function sourceIcon(source: string) {
  if (source === 'analyst_submission') return Users;
  if (source === 'opensanctions_fbi_lazarus') return Gavel;
  return FileText;
}

interface Props {
  matches: WalletTag[];
  emptyLabel?: string;
}

export function EvidenceTrail({ matches, emptyLabel }: Props) {
  if (matches.length === 0) {
    return (
      <div
        style={{ background: '#0C1118', border: '1px dashed #182030', borderRadius: 12, padding: 24, textAlign: 'center' }}
      >
        <p style={{ color: '#5A7A9A', fontSize: 13 }}>
          {emptyLabel ?? 'No citations on file for this address.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {matches.map((m, i) => {
        const Icon = sourceIcon(m.source);
        const c = CATEGORY_COLOR[m.category] ?? '#5A7A9A';
        return (
          <motion.div
            key={`${m.source}-${m.address_normalized}-${i}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: i * 0.04 }}
            whileHover={{ borderColor: `${c}50` }}
            style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: '14px 16px' }}
          >
            <div className="flex items-center justify-between gap-3 mb-2.5">
              <div className="flex items-center gap-2.5">
                <div style={{ width: 30, height: 30, borderRadius: '50%', border: '1.5px dashed #D9AE5C88', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={14} style={{ color: '#D9AE5C' }} />
                </div>
                <span
                  style={{ background: `${c}18`, color: c, border: `1px solid ${c}30`, fontSize: 10.5 }}
                  className="font-bold px-2.5 py-1 rounded-full tracking-wider uppercase"
                >
                  {CATEGORY_LABEL[m.category] ?? m.category}
                </span>
              </div>
              <AdmiraltyBadge grade={SOURCE_ADMIRALTY_GRADE[m.source]} compact />
            </div>
            <p style={{ color: '#DCE8F5', fontSize: 13, lineHeight: 1.5, marginBottom: 7 }}>
              {m.citation}
            </p>
            <div className="flex items-center justify-between">
              <span style={{ color: '#5A7A9A', fontSize: 11 }}>{m.source_date}</span>
              {m.source_label && (
                <span style={{ color: '#3A5A7A', fontSize: 11 }}>{m.source_label}</span>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
